import { useState } from 'react';
import { useActiveAccount } from 'thirdweb/react';
import { getContract, prepareContractCall, sendTransaction, waitForReceipt, readContract } from 'thirdweb';
import { polygon } from 'thirdweb/chains';
import { client } from '../thirdwebClient';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function useJoinTournament() {
  const account = useActiveAccount();
  const [state, setState] = useState({ step: 'idle', error: null });

  async function joinTournament({ tournament, gamePlayerId, gameField }) {
    if (!account) {
      setState({ step: 'error', error: 'Please connect your wallet first' });
      return;
    }
    if (!tournament.contract_address) {
      setState({ step: 'error', error: 'Tournament contract address is missing' });
      return;
    }

    try {
      // ── Step 1: Build tournament contract reference ──────────────────────
      const tournamentContract = getContract({
        client,
        chain: polygon,
        address: tournament.contract_address,
      });

      // ── Step 2: Read entry fee (use passed value or read from chain) ─────
      const entryFee = tournament.entry_fee
        ? BigInt(tournament.entry_fee)
        : await readContract({
            contract: tournamentContract,
            method: 'function entryFee() returns (uint256)',
            params: [],
          });

      // ── Step 3: Read USDC contract address from tournament contract ──────
      const usdcAddress = await readContract({
        contract: tournamentContract,
        method: 'function usdc() returns (address)',
        params: [],
      });

      // ── Step 4: Build USDC contract reference ────────────────────────────
      const usdcContract = getContract({
        client,
        chain: polygon,
        address: usdcAddress,
      });

      // ── Step 5: Approve USDC spend ───────────────────────────────────────
      setState({ step: 'approving', error: null });

      const approveTx = prepareContractCall({
        contract: usdcContract,
        method: 'function approve(address spender, uint256 amount) returns (bool)',
        params: [tournament.contract_address, entryFee],
      });

      // Wait for approve to be mined before calling joinTournament —
      // without this the allowance is still 0 when joinTournament runs.
      const approveResult = await sendTransaction({ transaction: approveTx, account });
      await waitForReceipt({ client, chain: polygon, transactionHash: approveResult.transactionHash });

      // ── Step 6: Join tournament on-chain ─────────────────────────────────
      setState({ step: 'joining', error: null });

      const joinTx = prepareContractCall({
        contract: tournamentContract,
        method: 'function joinTournament()',
        params: [],
      });

      const joinResult = await sendTransaction({ transaction: joinTx, account });

      // Wait for the join TX to be mined before sending the hash to the backend.
      // Without this the backend's RPC node hasn't indexed it yet → "TX not found".
      await waitForReceipt({ client, chain: polygon, transactionHash: joinResult.transactionHash });

      const txHash = joinResult.transactionHash;

      // ── Step 7: Record entry + fetch stats snapshot in backend ──────────
      setState({ step: 'fetching_stats', error: null });

      const payload = {
        tournamentId:  tournament.id,
        walletAddress: account.address,
        txHash,
        referralCode:  localStorage.getItem('referralCode') || undefined,
        [gameField]:   gamePlayerId,
      };

      // 90s timeout — backend fetches live stats before responding (can be slow)
      const { data } = await axios.post(`${API}/tournament/join`, payload, { timeout: 90_000 });

      setState({ step: 'done', error: null });
      return data;
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Transaction failed. Please try again.';
      setState({ step: 'error', error: msg });
      throw err;
    }
  }

  function reset() {
    setState({ step: 'idle', error: null });
  }

  return { joinTournament, state, reset };
}
